/**
 * Debug HUD for {@link BaseGameLoop}: stats.js FPS panel and Three.js addon inspector.
 */
import StatsJs from 'stats.js';
import * as THREE from 'three';
import { Inspector } from 'three/addons/inspector/Inspector.js';
import { Parameters } from 'three/addons/inspector/tabs/Parameters.js';
import { InspectorBase, setConsoleFunction } from 'three/webgpu';

import {
  applyThreeInspectorTogglePositioning,
  attachFloatingDraggableParametersGroup,
  type InspectorToggleOffset,
  type InspectorTogglePosition,
  nativeRendererHasInspectorSlot,
  setInspectorParametersGroupName
} from './debugInspectorOptions.js';
import { openChromeTraceInPerfetto } from './openPerfettoTrace.js';

import type { Renderer } from '../../render/Renderer.js';
import type { WebGPURenderer } from 'three/webgpu';

/** In-browser debug HUD driven by {@link BaseGameLoop}. */
export type DebugUIMode = 'none' | 'auto';

export interface GameLoopDebugUIOptions {
  renderer: Renderer;
  debugUIMode?: DebugUIMode;
  inspectorTogglePosition?: InspectorTogglePosition;
  inspectorToggleOffset?: InspectorToggleOffset;
  inspectorToggleScale?: number;
  onDownloadPerformanceSnapshot?: () => void;
  disablePerfCounter?: boolean;
}

export class GameLoopDebugUI {
  private stats: StatsJs | null = null;
  private inspector: Inspector | null = null;
  private disposeProfilerPanel: (() => void) | null = null;

  static get DEFAULT_OPTIONS(): Required<
    Pick<GameLoopDebugUIOptions, 'debugUIMode' | 'inspectorTogglePosition' | 'inspectorToggleOffset' | 'inspectorToggleScale'>
    > {
    return {
      debugUIMode: 'auto',
      inspectorTogglePosition: 'top-left',
      inspectorToggleOffset: { x: 10, y: 10 },
      inspectorToggleScale: 0.8,
    };
  }

  constructor(
    private readonly container: HTMLElement,
    private readonly options: GameLoopDebugUIOptions,
  ) {}

  /** Positions the game host, stats.js panel, and Three.js inspector when applicable. */
  public async init(): Promise<void> {
    if (this.options.debugUIMode === 'none') {
      return;
    }

    if (!this.options.disablePerfCounter) {
      const native = this.options.renderer.native;
      if (native.asWebGL()) {
        this.stats = new StatsJs();
        this.stats.dom.style.position = 'absolute';
        this.stats.dom.style.top = '10px';
        this.stats.dom.style.left = '10px';
        this.container.appendChild(this.stats.dom);
        this.stats.showPanel(0);
      } else {
        await this.setupInspector(native.asWebGPU()!);
      }
    }

    this.setupProfiler();
  }

  public beginStats(): void {
    this.stats?.begin();
  }

  public endStats(): void {
    this.stats?.end();
  }

  public dispose(): void {
    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = null;
    this.disposeInspector();
    this.stats?.dom.remove();
    this.stats = null;
  }

  /** @returns New visibility state, or `null` when no debug HUD element exists. */
  public toggleStatsVisibility(): boolean | null {
    const el = this.stats?.dom ?? this.inspector?.domElement;
    if (!el) {
      return null;
    }
    const hidden = el.style.display === 'none' || el.style.visibility === 'hidden';
    const visible = hidden;
    el.style.display = visible ? '' : 'none';
    el.style.visibility = visible ? 'visible' : 'hidden';
    return visible;
  }

  /** Active Three.js addon inspector when initialised; otherwise `null`. */
  public getInspector(): Inspector | null {
    return this.inspector;
  }

  /** Adds a named parameter group to the Three Inspector when available. */
  public createParametersGroup(name: string): ReturnType<Inspector['createParameters']> | null {
    return this.inspector?.createParameters(name) ?? null;
  }

  private async setupInspector(webgpuRenderer: WebGPURenderer): Promise<void> {
    this.inspector = new Inspector();
    webgpuRenderer.inspector = this.inspector;
    const el = this.inspector.domElement;
    const defaults = GameLoopDebugUI.DEFAULT_OPTIONS;
    applyThreeInspectorTogglePositioning(el, {
      position: this.options.inspectorTogglePosition ?? defaults.inspectorTogglePosition,
      offset: {
        x: this.options.inspectorToggleOffset?.x ?? defaults.inspectorToggleOffset.x,
        y: this.options.inspectorToggleOffset?.y ?? defaults.inspectorToggleOffset.y,
      },
      scale: this.options.inspectorToggleScale ?? defaults.inspectorToggleScale,
    });

    this.container.appendChild(el);
  }

  private setupProfiler(): void {
    const gui = new Parameters('').createGroup('Profiler');
    const actions = {
      toggleStatsProfiling: () => {
        if (THREE.ProfilerService.isEnabled()) {
          THREE.ProfilerService.disable();
        } else {
          THREE.ProfilerService.setProfile('stats');
          THREE.ProfilerService.enable();
        }
        updateUI();
      },
      toggleFullProfiling: () => {
        if (THREE.ProfilerService.isEnabled()) {
          const now = new Date();
          const p = (n: number) => `${n}`.padStart(2, '0');
          const stamp = `${now.getFullYear()}-${p(now.getMonth() + 1)}-${p(now.getDate())}_${p(now.getHours())}-${p(now.getMinutes())}-${p(now.getSeconds())}`;
          const filename = `gnsx-trace-${stamp}.json`;
          const trace = THREE.ProfilerService.exportChromeTrace();
          THREE.ProfilerService.downloadTrace(filename);
          if (trace.traceEvents.length > 2) {
            openChromeTraceInPerfetto(JSON.stringify(trace), { fileName: filename });
          }
          THREE.ProfilerService.disable();
        } else {
          THREE.ProfilerService.setProfile('full');
          THREE.ProfilerService.enable();
        }
        updateUI();
      },
      downloadSnapshot: () => {
        const wasRunning = THREE.ProfilerService.isEnabled();
        // Capture while profiler is still enabled so timing data is included.
        this.options.onDownloadPerformanceSnapshot?.();
        if (wasRunning) {
          THREE.ProfilerService.disable();
        }
        updateUI();
      },
      report: () => THREE.ProfilerService.report(),
    };

    const statsCtrl = gui.add(actions, 'toggleStatsProfiling');
    const snapshotCtrl = gui.add(actions, 'downloadSnapshot');
    const fullCtrl = gui.add(actions, 'toggleFullProfiling');
    gui.add(actions, 'report').name('Report → console');

    const updateUI = () => {
      const titleName = THREE.ProfilerService.isEnabled() ? `Profiling (${THREE.ProfilerService.getProfile()})` : 'Profiler';
      setInspectorParametersGroupName(gui, titleName);
      statsCtrl.name(
        THREE.ProfilerService.isEnabled() ? 'Stop' : '▶ Stats Profiling',
      );
      snapshotCtrl.name(
        THREE.ProfilerService.isEnabled() ? 'Stop + Download Snapshot' : 'Download Quick Snapshot',
      );
      fullCtrl.name(
        THREE.ProfilerService.isEnabled() ? 'Stop + Download Trace' : '▶ Full Profiling',
      );
    };
    updateUI();

    this.disposeProfilerPanel?.();
    this.disposeProfilerPanel = attachFloatingDraggableParametersGroup(this.container, gui);
  }

  private disposeInspector(): void {
    const renderer = this.options.renderer;
    if (!this.inspector) {
      return;
    }
    const inspector = this.inspector as any;
    try {
      const genesysRenderer = renderer.native;
      const native = genesysRenderer.asWebGL() ?? genesysRenderer.asWebGPU();
      if (nativeRendererHasInspectorSlot(native)) {
        native.inspector = new InspectorBase();
      }

      const safeInspectorRenderer = {
        init: async () => { },
        _nodes: { nodeFrame: { deltaTime: 0, frameId: 0 } },
        resolveTimestampsAsync: async () => { },
        hasFeature: () => false,
        backend: {
          trackTimestamp: false,
          getTimestampFrames: () => [] as number[],
          hasTimestamp: () => false,
          getTimestamp: () => 0,
        },
      };

      const originalSetRenderer = inspector.setRenderer?.bind(inspector);
      const activeRenderer = inspector.getRenderer?.() as {
        resolveTimestampsAsync?: () => Promise<void>;
        backend?: { trackTimestamp?: boolean };
      } | null;
      if (activeRenderer) {
        activeRenderer.resolveTimestampsAsync = async () => { };
        if (activeRenderer.backend && 'trackTimestamp' in activeRenderer.backend) {
          activeRenderer.backend.trackTimestamp = false;
        }
      }
      setConsoleFunction((type: 'log' | 'warn' | 'error', message: string, ...params: unknown[]) => {
        if (type === 'error' && message.includes('THREE.Error resolving queries:')) {
          return;
        }
        if (type === 'warn') {
          console.warn(message, ...params);
          return;
        }
        if (type === 'error') {
          console.error(message, ...params);
          return;
        }
        console.log(message, ...params);
      });
      inspector.resolveTimestamp = async () => { };
      inspector._resolveTimestampPromise = null;
      inspector.addFrame = () => { };
      inspector._renderer = safeInspectorRenderer;
      if (originalSetRenderer) {
        inspector.setRenderer = (r: unknown) => {
          if (r === null) {
            return inspector;
          }
          if (
            typeof r === 'object'
            && r !== null
            && typeof (r as { init?: unknown }).init === 'function'
          ) {
            originalSetRenderer(r);
          }
          return inspector;
        };
      }
    } catch (error) {
      console.warn('[GameLoop] Failed to reset renderer inspector:', error);
    }
    this.inspector.domElement.remove();
    this.inspector = null;
  }
}
